/**
 * TanStack Query hooks. Query keys mirror API paths, so a cache invalidation reads like the
 * endpoint it refers to.
 */

import * as React from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type {
  AppConfig,
  AppReleases,
  CurrentBuild,
  Game,
  Me,
  NotificationFeed,
  TeamMemberProfile,
  TeamResponse,
  UpdatesResponse,
  Platform,
} from '@goodbuild/shared';
import { ApiRequestError, api } from './client.js';
import { buildPlatform } from '../platform/index.js';

export const keys = {
  me: ['me'] as const,
  plans: ['plans'] as const,
  appConfig: ['app', 'config'] as const,
  appReleases: ['app', 'releases'] as const,
  games: ['games'] as const,
  game: (slug: string) => ['games', slug] as const,
  currentBuild: (slug: string, platform: Platform) => ['games', slug, 'builds', platform] as const,
  updates: (installed: string, platform: Platform) =>
    ['library', 'updates', platform, installed] as const,
  team: ['team'] as const,
  notifications: ['notifications'] as const,
  admin: {
    overview: ['admin'] as const,
    games: ['admin', 'games'] as const,
    team: ['admin', 'team'] as const,
    roles: ['admin', 'roles'] as const,
    storage: ['admin', 'storage'] as const,
    webhooks: ['admin', 'webhooks'] as const,
    apiKeys: ['admin', 'api-keys'] as const,
    audit: ['admin', 'audit'] as const,
    insights: ['admin', 'insights'] as const,
    users: (q: string) => ['admin', 'users', q] as const,
    build: (id: string) => ['admin', 'builds', id] as const,
    gameCredits: (id: string) => ['admin', 'games', id, 'credits'] as const,
  },
};

/**
 * The signed-in user and their Access, or null for a visitor.
 *
 * Not being signed in is an answer, not a failure: the whole app is browsable without an
 * account, so a 401 here resolves to null rather than leaving the query in an error state that
 * every screen would then have to interpret.
 *
 * Refetched on window focus and every 5 minutes, so losing Access locks the paid parts of the UI
 * without a reload (`03-client-app.md` section 1.2).
 */
export function useMe(options?: Partial<UseQueryOptions<Me | null>>) {
  return useQuery<Me | null>({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return await api.get<Me>('/me');
      } catch (err) {
        if (err instanceof ApiRequestError && err.needsSignIn) return null;
        throw err;
      }
    },
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
    retry: false,
    staleTime: 30_000,
    ...options,
  });
}

export function useAccess() {
  const { data, isLoading } = useMe();
  return {
    me: data ?? null,
    /** True only while the first `/me` is in flight: a gate must not blink past a supporter. */
    loading: isLoading,
    signedIn: Boolean(data),
    hasAccess: Boolean(data?.access.hasAccess),
    isAdmin: data?.user.role === 'admin',
    isStaff: data?.user.role === 'admin' || data?.user.role === 'dev',
    accessUntil: data?.access.accessUntil ?? null,
  };
}

export function useAppConfig() {
  return useQuery<AppConfig>({
    queryKey: keys.appConfig,
    queryFn: () => api.get<AppConfig>('/app/config'),
    staleTime: 10 * 60 * 1000,
  });
}

/** The installer links on `/download`, resolved from the release manifests by the API. */
export function useAppReleases() {
  return useQuery<AppReleases>({
    queryKey: keys.appReleases,
    queryFn: () => api.get<AppReleases>('/app/releases'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useGames() {
  return useQuery<Game[]>({
    queryKey: keys.games,
    queryFn: async () => (await api.get<{ games: Game[] }>('/games')).games,
    staleTime: 60_000,
  });
}

export function useGame(slug: string) {
  return useQuery<Game>({
    queryKey: keys.game(slug),
    queryFn: async () => (await api.get<{ game: Game }>(`/games/${slug}`)).game,
    enabled: Boolean(slug),
  });
}

/**
 * The people at the studio, with what each of them has worked on.
 *
 * Public, so it is not gated on being signed in, and cached for a good while: the team changes
 * about as often as the studio's address does, and the studio screen is one someone lands on
 * from outside and scrolls straight down.
 */
export function useTeam() {
  return useQuery<TeamMemberProfile[]>({
    queryKey: keys.team,
    queryFn: async () => (await api.get<TeamResponse>('/team')).members,
    staleTime: 10 * 60 * 1000,
  });
}

export function useCurrentBuild(slug: string, enabled = true) {
  const platform = buildPlatform;
  return useQuery<CurrentBuild>({
    queryKey: keys.currentBuild(slug, (platform ?? 'windows') as Platform),
    queryFn: () => api.get<CurrentBuild>(`/games/${slug}/builds/current`, { platform }),
    enabled: enabled && Boolean(platform) && Boolean(slug),
    retry: false,
  });
}

/** Polled every 10 minutes and on focus, which is what surfaces "Update available". */
export function useLibraryUpdates(installed: Array<{ gameId: string; buildId: string }>) {
  const platform = buildPlatform;
  const encoded = installed.map((i) => `${i.gameId}:${i.buildId}`).join(',');
  return useQuery<UpdatesResponse>({
    queryKey: keys.updates(encoded, (platform ?? 'windows') as Platform),
    queryFn: () => api.get<UpdatesResponse>('/library/updates', { installed: encoded, platform }),
    enabled: Boolean(platform) && installed.length > 0,
    refetchInterval: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

/* Needs a signed-in account rather than Access, so a signed-out visit makes no request at all. */

export function useNotifications() {
  const { signedIn } = useAccess();
  return useQuery<NotificationFeed>({
    queryKey: keys.notifications,
    queryFn: () => api.get<NotificationFeed>('/notifications'),
    enabled: signedIn,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/notifications/read'),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.notifications }),
  });
}

/** How long a return from checkout waits for the webhook before it stops spinning. */
export const AWAIT_ACCESS_TIMEOUT_MS = 20 * 1000;
const AWAIT_ACCESS_POLL_MS = 2000;

/**
 * Waits for Access to appear after a checkout, by asking `/me` again until it does.
 *
 * A browser coming back from the payment page proves somebody got through the card form. It
 * does not prove the money moved, and it says nothing about whether the webhook that grants
 * Access has arrived yet -- the redirect usually wins that race. Polling is what closes the gap
 * without trusting the redirect for something only the webhook can say.
 *
 * It gives up rather than spinning forever. A webhook that has not landed in twenty seconds is
 * either slow or is not coming, and both of those are better answered with words than with an
 * indefinite spinner over a payment that already succeeded.
 */
export function useAwaitAccess(active: boolean): { givenUp: boolean } {
  const qc = useQueryClient();
  const [givenUp, setGivenUp] = React.useState(false);

  React.useEffect(() => {
    if (!active) {
      setGivenUp(false);
      return;
    }

    const deadline = Date.now() + AWAIT_ACCESS_TIMEOUT_MS;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      if (cancelled) return;
      try {
        await qc.refetchQueries({ queryKey: keys.me });
      } catch {
        // A failed poll is not an answer. The deadline below is what ends this, so one dropped
        // request must not be reported as "you did not pay".
      }
      if (cancelled) return;
      if (Date.now() >= deadline) {
        setGivenUp(true);
        return;
      }
      timer = setTimeout(() => void tick(), AWAIT_ACCESS_POLL_MS);
    };

    timer = setTimeout(() => void tick(), AWAIT_ACCESS_POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [active, qc]);

  return { givenUp };
}
